import { nativeIncidentRequest } from '../lib/incidentTransport';
import {IncidentPanel} from './IncidentsPage';
import { selectRuntimeSession, readStoredRuntimeSession } from "../lib/runtimeSessionSelection";
import VisitsPanel from "../components/VisitsPanel";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Helper para pedir permisos de geolocalización.
async function requestLocationPermission() {
  if (typeof navigator === "undefined" || !navigator.geolocation) return false;

  try {
    if (navigator.permissions?.query) {
      const result = await navigator.permissions.query({ name: "geolocation" });
      if (result.state === "granted") return true;
    }

    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        () => resolve(false),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 60000 },
      );
    });
  } catch {
    return false;
  }
}

function getStorageItem(key) {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

function setStorageItem(key, value) {
  if (!value) return;
  try {
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
}

function readRuntimeSessionFromStorage() {
  try {
    return selectRuntimeSession({}, readStoredRuntimeSession(window.localStorage) || readStoredRuntimeSession(window.sessionStorage));
  } catch {
    return selectRuntimeSession({}, null);
  }
}

function syncRuntimeSession(session) {
  if (!session) return;
  setStorageItem("tracker_runtime_token", session.runtimeToken);
  setStorageItem("tracker_access_token", session.runtimeToken);
  setStorageItem("tracker_user_id", session.trackerUserId);
  setStorageItem("user_id", session.trackerUserId);
  setStorageItem("tracker_org_id", session.orgId);
  setStorageItem("org_id", session.orgId);
}

function readRuntimeSessionFromUrl() {
  const params = new URLSearchParams(window.location.search);

  return {
    inviteToken: params.get("inviteToken") || params.get("invite_token") || "",
    runtimeToken:
      params.get("tracker_runtime_token") ||
      params.get("runtimeToken") ||
      params.get("token") ||
      "",
    trackerUserId:
      params.get("tracker_user_id") ||
      params.get("trackerUserId") ||
      params.get("user_id") ||
      "",
    orgId:
      params.get("org_id") ||
      params.get("orgId") ||
      "",
  };
}

function getNativeBridge() {
  if (typeof window === "undefined") return null;
  return window.AndroidBridge || window.Android || null;
}

const PREVIEW_HOSTNAME = "preview.tugeocercas.com";

function openPreviewApp(session) {
  const params = new URLSearchParams({
    tracker_runtime_token: session.runtimeToken || "",
    tracker_user_id: session.trackerUserId || "",
    org_id: session.orgId || "",
  });
  window.location.href = `geocercas-preview://tracker?${params.toString()}`;
}

function callNativeBridge(bridge, session) {
  if (!bridge || !session?.runtimeToken || !session?.orgId) return false;

  const { runtimeToken, trackerUserId, orgId } = session;

  console.log("[TRACKER] bridge exists?", {
    bridgeType: typeof bridge,
    hasSaveTrackerSession: typeof bridge.saveTrackerSession === "function",
    hasSaveSession: typeof bridge.saveSession === "function",
    hasSetTrackerSession: typeof bridge.setTrackerSession === "function",
    hasRequestStartTracking: typeof bridge.requestStartTracking === "function",
    hasStartTracking: typeof bridge.startTracking === "function",
    runtimeToken: !!runtimeToken,
    trackerUserId: !!trackerUserId,
    orgId: !!orgId,
  });

  try {
    if (typeof bridge.saveTrackerSession === "function") {
      bridge.saveTrackerSession(runtimeToken, trackerUserId || "", orgId);
    } else if (typeof bridge.saveSession === "function") {
      bridge.saveSession(runtimeToken, trackerUserId || "", orgId);
    } else if (typeof bridge.setTrackerSession === "function") {
      bridge.setTrackerSession(runtimeToken, trackerUserId || "", orgId);
    }

    if (typeof bridge.startTracking === "function") {
      bridge.startTracking(runtimeToken, trackerUserId || "", orgId);
    } else if (typeof bridge.requestStartTracking === "function") {
      bridge.requestStartTracking();
    } else {
      return false;
    }

    return true;
  } catch (err) {
    console.error("[TRACKER] native bridge call failed", err);
    return false;
  }
}

function openNativeSetting(methodNames, fallbackMessage) {
  const bridge = getNativeBridge();
  const names = Array.isArray(methodNames) ? methodNames : [methodNames];

  for (const methodName of names) {
    if (bridge && typeof bridge[methodName] === "function") {
      bridge[methodName]();
      return;
    }
  }

  alert(fallbackMessage);
}

export default function TrackerGpsPage() {
  const { t } = useTranslation();

  const [runtimeSession, setRuntimeSession] = useState(() => {
    const fromStorage = readRuntimeSessionFromStorage();
    return fromStorage;
  });

  const [msg, setMsg] = useState(() => t("tracker.gps.messageStarting"));
  const [nativeBridgeReady, setNativeBridgeReady] = useState(null);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);

  const [debugInfo, setDebugInfo] = useState({
    hasRuntimeToken: false,
    hasTrackerUserId: false,
    hasOrgId: false,
    nativeMode: true,
    bridgeFound: false,
    lastCheckAt: null,
    lastError: null,
  });

  const disposedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const permissionRequestedRef = useRef(false);

  const ready = useMemo(() => {
    return Boolean(runtimeSession.runtimeToken && runtimeSession.trackerUserId && runtimeSession.orgId);
  }, [runtimeSession]);

  const trackingActive = ready && nativeBridgeReady === true;
  const needsNativeApp = ready && nativeBridgeReady === false;

  useEffect(() => {
    disposedRef.current = false;

    const fromUrl = readRuntimeSessionFromUrl();
    const fromStorage = readRuntimeSessionFromStorage();

    const merged = selectRuntimeSession(fromUrl, fromStorage);

    if (fromUrl.inviteToken && !permissionRequestedRef.current) {
      permissionRequestedRef.current = true;
      requestLocationPermission().then((granted) => {
        console.log("[TRACKER_LOCATION_PERMISSION]", { granted });
      });
    }

    if (merged.runtimeToken || merged.trackerUserId || merged.orgId) {
      console.log("[TRACKER_QUERY_PARAMS_FOUND]", {
        runtimeToken: !!merged.runtimeToken,
        trackerUserId: !!merged.trackerUserId,
        orgId: !!merged.orgId,
      });

      syncRuntimeSession(merged);
      setRuntimeSession(merged);
    }

    return () => {
      disposedRef.current = true;
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!ready || disposedRef.current) return;

    let cancelled = false;

    const startNativeTracking = async () => {
      const availableBridge = getNativeBridge();
      if (availableBridge && !permissionRequestedRef.current) {
        permissionRequestedRef.current = true;
        await requestLocationPermission();
      }

      if (cancelled || disposedRef.current) return;

      console.log("[TRACKER] JS tracking disabled, using native service only");

      const bridge = getNativeBridge();
      const bridgeStarted = callNativeBridge(bridge, runtimeSession);
      const bridgeReady = Boolean(bridge && bridgeStarted);

      if (!bridge) {
        console.warn("[TRACKER] native bridge not found");
      } else if (!bridgeStarted) {
        console.warn("[TRACKER] native bridge did not start tracking");
      }

      setNativeBridgeReady(bridgeReady);

      setMsg(bridgeReady ? t("tracker.gps.messageActive") : t(window.location.hostname === "preview.tugeocercas.com" ? "tracker.gps.browserHelpPreview" : "tracker.gps.browserHelp"));

      setDebugInfo((prev) => ({
        ...prev,
        hasRuntimeToken: !!runtimeSession.runtimeToken,
        hasTrackerUserId: !!runtimeSession.trackerUserId,
        hasOrgId: !!runtimeSession.orgId,
        nativeMode: true,
        bridgeFound: !!bridge,
        lastCheckAt: new Date().toISOString(),
        lastError: null,
      }));

      console.log("[TRACKER_FINAL_SESSION_SNAPSHOT]", {
        hasRuntimeToken: !!runtimeSession.runtimeToken,
        hasTrackerUserId: !!runtimeSession.trackerUserId,
        hasOrgId: !!runtimeSession.orgId,
        bridgeFound: !!bridge,
        bridgeStarted,
      });
    };

    startNativeTracking();

    return () => {
      cancelled = true;
    };
  }, [ready, runtimeSession, t]);

  useEffect(() => {
    if (ready) return;

    let cancelled = false;
    const startedAt = Date.now();

    const poll = () => {
      if (cancelled || disposedRef.current) return;

      const stored = readRuntimeSessionFromStorage();
      const hasSession = Boolean(stored.runtimeToken && stored.trackerUserId && stored.orgId);

      if (hasSession) {
        console.log("[TRACKER_POLL] runtime session detected");
        setSessionUnavailable(false);
        syncRuntimeSession(stored);
        setRuntimeSession(stored);
        setMsg(t("tracker.gps.messagePreparing"));
        return;
      }

      if (Date.now() - startedAt >= 12000) {
        setSessionUnavailable(true);
      }
      pollTimerRef.current = window.setTimeout(poll, 1000);
    };

    window.addEventListener("geocercas-native-session-ready", checkNativeSession);
    function checkNativeSession() {
      if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
      poll();
    }
    poll();

    return () => {
      window.removeEventListener("geocercas-native-session-ready", checkNativeSession);
      cancelled = true;
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [ready, t]);

  const pageStyle = {
    minHeight: "100vh",
    background: "#f5f7fb",
    padding: "24px 16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  };

  const cardStyle = {
    width: "100%",
    maxWidth: 560,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 24,
    marginTop: 24,
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.06)",
    textAlign: "center",
  };

  const iconStyle = (isReady) => ({
    width: 56,
    height: 56,
    margin: "0 auto 16px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    background: isReady ? "#ecfdf3" : "#eff6ff",
    border: isReady ? "1px solid #bbf7d0" : "1px solid #bfdbfe",
  });

  const titleStyle = {
    fontSize: 22,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 8,
  };

  const subtitleStyle = {
    fontSize: 15,
    lineHeight: 1.5,
    color: "#4b5563",
    marginBottom: 16,
  };

  const badgeStyle = (isReady) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 600,
    background: isReady ? "#ecfdf3" : "#eff6ff",
    color: isReady ? "#166534" : "#1d4ed8",
    border: isReady ? "1px solid #bbf7d0" : "1px solid #bfdbfe",
  });

  const noteStyle = {
    marginTop: 16,
    fontSize: 13,
    color: "#6b7280",
    lineHeight: 1.45,
  };

  const criticalPermissionsPanelStyle = {
    marginTop: 32,
    padding: 20,
    border: "1.5px solid #fbbf24",
    borderRadius: 14,
    background: "#fffbea",
    textAlign: "left",
  };

  const permissionButtonStyle = {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #eab308",
    background: "#fef9c3",
    color: "#78350f",
    fontWeight: 600,
    cursor: "pointer",
    minWidth: 170,
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={iconStyle(trackingActive)}>{trackingActive ? "\u2705" : "\u{1F4CD}"}</div>

        <div style={titleStyle}>
          {trackingActive
            ? t("tracker.gps.titleActive")
            : sessionUnavailable ? t("tracker.gps.sessionMissingTitle") : needsNativeApp ? t("tracker.gps.browserTitle") : t("tracker.gps.titleStarting")}
        </div>

        <div style={subtitleStyle}>
          {trackingActive
            ? t("tracker.gps.subtitleActive")
            : sessionUnavailable ? t("tracker.gps.sessionMissingHelp") : needsNativeApp ? t("tracker.gps.browserSubtitle") : t("tracker.gps.subtitleStarting")}
        </div>

        <div style={badgeStyle(trackingActive)}>
          <span>
            {trackingActive
              ? t("tracker.gps.badgeActive")
              : sessionUnavailable ? t("tracker.gps.browserBadge") : needsNativeApp ? t("tracker.gps.browserBadge") : t("tracker.gps.badgeInitializing")}
          </span>
        </div>

        {ready && <VisitsPanel key={runtimeSession.orgId+runtimeSession.trackerUserId} orgId={runtimeSession.orgId} runtime={runtimeSession} />}
        {ready && ["preview.tugeocercas.com", "app.tugeocercas.com"].includes(window.location.hostname) && nativeIncidentRequest() && <details className="mt-4 text-left"><summary className="rounded-xl bg-emerald-700 p-3 text-white cursor-pointer">{t('app.tabs.incidents')}</summary><IncidentPanel key={'incidents:'+runtimeSession.orgId+runtimeSession.trackerUserId} org={runtimeSession.orgId} identityUser={runtimeSession.trackerUserId} runtime={runtimeSession}/></details>}

        {!!msg && !sessionUnavailable && (
          <div style={noteStyle}>
            {msg}
          </div>
        )}

        {needsNativeApp && window.location.hostname === PREVIEW_HOSTNAME && (
          <button
            type="button"
            style={{ ...permissionButtonStyle, marginTop: 16 }}
            onClick={() => openPreviewApp(runtimeSession)}
          >
            {t("tracker.gps.openPreviewApp")}
          </button>
        )}

        {/* Critical tracker permissions panel */}
        <div style={criticalPermissionsPanelStyle} hidden={needsNativeApp}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: "#b45309" }}>
            {t("trackerGps.perms.panelTitle")}
          </div>

          <div style={{ fontSize: 14, color: "#92400e", marginBottom: 16, lineHeight: 1.45 }}>
            {t("trackerGps.perms.panelDesc")}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <button
              type="button"
              style={permissionButtonStyle}
              onClick={() => {
                openNativeSetting(
                  "requestLocationPermissions",
                  t("trackerGps.perms.locationAlwaysAlert"),
                );
              }}
            >
              {t("trackerGps.perms.locationAlways")}
            </button>

            <button
              type="button"
              style={permissionButtonStyle}
              onClick={() => {
                openNativeSetting(
                  ["requestIgnoreBatteryOptimizations", "openAppBatterySettings"],
                  t("trackerGps.perms.batteryUnrestrictedAlert"),
                );
              }}
            >
              {t("trackerGps.perms.batteryUnrestricted")}
            </button>

            <button
              type="button"
              style={permissionButtonStyle}
              onClick={() => {
                openNativeSetting(
                  "openAutoStartSettings",
                  t("trackerGps.perms.autostartAlert"),
                );
              }}
            >
              {t("trackerGps.perms.autostart")}
            </button>

            <button
              type="button"
              style={permissionButtonStyle}
              onClick={() => {
                openNativeSetting(
                  "openAppBatterySettings",
                  t("trackerGps.perms.backgroundDataAlert"),
                );
              }}
            >
              {t("trackerGps.perms.backgroundData")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
